/* eslint-disable consistent-return,max-len */
import SimpleSchema from 'simpl-schema';
import { map, pick, values } from 'lodash';

export const CONNECTOR_SUBTYPES = {
  VECTOR: 'VECTOR',
  RASTER: 'RASTER',
  POINTCLOUD: 'POINTCLOUD',
  THEMATIC: 'THEMATIC',
  GENERIC: 'GENERIC',
  properties: {
    VECTOR: {
      name: 'VECTOR',
      value: 'VECTOR',
      humanValue: 'Vector'
    },
    RASTER: {
      name: 'RASTER',
      value: 'RASTER',
      humanValue: 'Raster'
    },
    POINTCLOUD: {
      name: 'POINTCLOUD',
      value: 'POINTCLOUD',
      humanValue: 'PointCloud'
    },
    THEMATIC: {
      name: 'THEMATIC',
      value: 'THEMATIC',
      humanValue: 'Thematic'
    },
    GENERIC: {
      name: 'GENERIC',
      value: 'GENERIC',
      humanValue: 'Generic'
    }
  }
};

export const FTP_SUBTYPES = pick(CONNECTOR_SUBTYPES, [
  'VECTOR',
  'RASTER',
  'POINTCLOUD',
  'THEMATIC',
  'GENERIC'
]);

export const CONNECTOR_TYPES = {
  POSTGRESQL: 'PostgreSQL',
  properties: {
    POSTGRESQL: {
      name: 'POSTGRESQL',
      value: 'PostgreSQL',
      humanValue: 'PostgreSQL'
    }
  }
};

export const POSTGRESQL_SUBTYPES = pick(CONNECTOR_SUBTYPES, ['VECTOR', 'THEMATIC']);

export const HTTP_SUBTYPES = pick(CONNECTOR_SUBTYPES, [
  'VECTOR',
  'RASTER',
  'POINTCLOUD',
  'THEMATIC',
  'GENERIC'
]);

export const GEOJSONVT_SUBTYPES = pick(CONNECTOR_SUBTYPES, ['VECTOR']);

export const getSubTypesForType = (connectorType, resultFormat = 'array') => {
  if (resultFormat === 'object') {
    switch (connectorType) {
      case CONNECTOR_TYPES.FTP:
      case CONNECTOR_TYPES.WMS:
        return FTP_SUBTYPES;
      case CONNECTOR_TYPES.POSTGRESQL:
        return POSTGRESQL_SUBTYPES;
      case CONNECTOR_TYPES.HTTP:
        return HTTP_SUBTYPES;
      case CONNECTOR_TYPES.GEOJSONVT:
        return GEOJSONVT_SUBTYPES;
      default:
        return {};
    }
  }
  switch (connectorType) {
    case CONNECTOR_TYPES.FTP:
    case CONNECTOR_TYPES.WMS:
      return values(FTP_SUBTYPES);
    case CONNECTOR_TYPES.POSTGRESQL:
      return values(POSTGRESQL_SUBTYPES);
    case CONNECTOR_TYPES.HTTP:
      return values(HTTP_SUBTYPES);
    case CONNECTOR_TYPES.GEOJSONVT:
      return values(GEOJSONVT_SUBTYPES);
    default:
      return [];
  }
};

const componenteTypeDataSchema = new SimpleSchema({
  name: {
    type: String,
    label: 'The name to show the component'
  },
  show: {
    type: Boolean,
    label: 'Flag that checks if each component is shown'
  }
});
export const connectorSchema = new SimpleSchema({
  createdAt: {
    type: String,
    label: 'The date this connection was created in the database',
    optional: true,
    autoValue() {
      if (this.isInsert) return new Date().toISOString();
    }
  },
  deleted: {
    type: String,
    label: 'The date the connection was deleted',
    optional: true,
    autoValue() {
      if (this.isInsert) return 'no';
    }
  },
  _id: {
    type: String,
    label: 'The user for the connection',
    optional: true
  },
  isOutput: {
    type: Boolean,
    label: 'Flag to check if the connector is an output one',
    optional: true
  },
  name: {
    type: String,
    label: 'A name to identify the connection',
    optional: true
  },
  owner: {
    type: String,
    label: 'The ID of the user this connection belongs to',
    optional: true
  },
  subtype: {
    type: String,
    label: 'The subtype of the connection added',
    optional: true
  },
  type: {
    type: String,
    label: 'The type of the connection added',
    allowedValues: map(CONNECTOR_TYPES.properties, 'value'),
    optional: true
  },
  updatedAt: {
    type: String,
    label: 'The date this connection was last updated',
    optional: true,
    autoValue() {
      if (this.isInsert || this.isUpdate) return new Date().toISOString();
    }
  },
  databaseName: {
    type: String,
    label: 'The name of the database for the connection',
    optional: true
  },
  domain: {
    type: String,
    label: 'The domain for the connection',
    optional: true
  },
  filePath: {
    type: String,
    label: 'The file path for the connection',
    optional: true
  },
  fileType: {
    type: String,
    label: 'The file type for the connection',
    optional: true
  },
  ip: {
    type: String,
    label: 'The ip address for the connection',
    optional: true
  },
  password: {
    type: String,
    label: 'The password for the connection',
    optional: true
  },
  port: {
    type: SimpleSchema.Integer,
    label: 'The port for the connection',
    optional: true,
    min: 0,
    max: 65535
  },
  schema: {
    type: String,
    label: 'The name of the schema of the database',
    optional: true
  },
  user: {
    type: String,
    label: 'The user for the connection',
    optional: true
  }
});
export const serverSchema = new SimpleSchema({
  createdAt: {
    type: String,
    label: 'The date this telemetry connection was created in the database.',
    autoValue() {
      if (this.isInsert) return new Date().toISOString();
    },
    optional: true
  },
  domain: {
    type: String,
    label: 'The domain, included protocol for the server connection',
    optional: true
  },
  _id: {
    type: String,
    label: 'The ip for the telemetry connection',
    optional: true
  },
  ip: {
    type: String,
    label: 'The ip for the telemetry connection',
    optional: true
  },
  name: {
    type: String,
    label: 'A name to identify the telemetry connection.',
    optional: true
  },
  owner: {
    type: String,
    label: 'The ID of the user this telemetry connection belongs to.',
    optional: true
  },
  port: {
    type: Number,
    label: 'The port for the telemetry connection',
    optional: true
  },
  url: {
    type: String,
    label: 'The url for the telemetry connection',
    optional: true
  },
  updatedAt: {
    type: String,
    label: 'The date this telemetry connection was last updated.',
    autoValue() {
      if (this.isInsert || this.isUpdate) return new Date().toISOString();
    },
    optional: true
  },
  selected: {
    type: Boolean,
    label: 'Flag to check which telemetry connection to use',
    autoValue() {
      if (this.isInsert) return false;
    },
    optional: true
  },
  serverType: {
    type: String,
    label:
      'The server Type, so far GMS/SGMS (GeoMicroServices) and DSL/SDSL (Database Server Layer)',
    optional: true
  }
});

const entWineDataSchema = new SimpleSchema({
  name: {
    type: String,
    label: 'The name to show of the entWine',
    optional: true
  },
  path: {
    type: String,
    label: 'The link of the entWine',
    optional: true
  },
  videoId: {
    type: String,
    label: 'The id of video associated at profile',
    optional: true
  },
  buffer: {
    type: String,
    label: 'The metres for profile buffer',
    optional: true
  },
  clasify: {
    type: Array,
    label: 'The names for the filtered points in profile',
    optional: true
  },
  'clasify.$': {
    type: String,
    label: 'The names for the filtered points in profile',
    optional: true
  },
  profileId: {
    type: String,
    label: 'The id of the profile asociated',
    optional: true
  }
});

export const settingsConnectionSchema = new SimpleSchema({
  connector: {
    type: connectorSchema,
    label: 'The server connection to use in DownloadLink.',
    optional: true
  },
  importFile: {
    type: Boolean,
    label: 'The option to show import File.',
    optional: true
  },
  server: {
    type: serverSchema,
    label: 'The server connection to use in DownloadLink.',
    optional: true
  },
  source: {
    type: Array,
    label: 'The source.',
    optional: true
  },
  'source.$': {
    type: entWineDataSchema
  }
});

const sizeDrawerDataSchema = new SimpleSchema({
  name: {
    type: String,
    label: 'The name to show the sizeDrawer'
  },
  value: {
    type: Number,
    label: 'The value of the sizeDrawer'
  }
});

const mapTypeDataSchema = new SimpleSchema({
  name: {
    type: String,
    label: 'The name to show the component'
  },
  value: {
    type: String,
    label: 'Flag that checks if each component is shown'
  }
});

const videoOptionsDataSchema = new SimpleSchema({
  name: {
    type: String,
    label: 'The name to show the component',
    optional: true
  },
  url: {
    type: String,
    label: 'The name to show the component',
    optional: true
  },
  value: {
    type: String,
    label: 'Flag that checks if each component is shown',
    optional: true
  }
});

const directButtonsDataSchema = new SimpleSchema({
  intervalTime: {
    type: String,
    label: 'The value of the interval to create the annotation.',
    optional: true
  },
  projectId: {
    type: String,
    label: 'The id of the project.',
    optional: true
  },
  savingSystem: {
    type: String,
    label: 'The annotation saving system.',
    optional: true
  },
  templates: {
    type: Array,
    label: 'The list of ids of the annotation templates.',
    optional: true
  },
  'templates.$': {
    type: String,
    label: 'The id of the templates.',
    optional: true
  },
  videoId: {
    type: String,
    label: 'The id of the video.',
    optional: true
  },
  videoOptions: {
    type: Array,
    label: 'The options of the video.',
    optional: true
  },
  'videoOptions.$': {
    type: videoOptionsDataSchema,
    label: 'The options of the video.'
  }
});

const selectedComponentsDataSchema = new SimpleSchema({
  AnnotationDirectButtons: {
    type: componenteTypeDataSchema,
    label: 'Flag that checks if the AnnotationDirectButtons button is shown'
  },
  DownloadLinksComponent: {
    type: componenteTypeDataSchema,
    label: 'Flag that checks if the DownloadLinksComponent button is shown'
  },
  FileToolsComponent: {
    type: componenteTypeDataSchema,
    label: 'Flag that checks if the FileToolsComponent button is shown'
  },
  IntroductionMapComponent: {
    type: componenteTypeDataSchema,
    label: 'Flag that checks if the IntroductionMapComponent button is shown'
  },
  InfoLayerSelection: {
    type: componenteTypeDataSchema,
    label: 'Flag that checks if the InfoLayerSelection button is shown'
  },
  ISLComponent: {
    type: componenteTypeDataSchema,
    label: 'Flag that checks if the ISLComponent button is shown'
  },
  LayersControl: {
    type: componenteTypeDataSchema,
    label: 'Flag that checks if the LayersControl button is shown'
  },
  NotesComponent: {
    type: componenteTypeDataSchema,
    label: 'Flag that checks if the NotesComponent button is shown'
  },
  PointMapComponent: {
    type: componenteTypeDataSchema,
    label: 'Flag that checks if the PointMapComponent button is shown'
  },
  SentinelComponent: {
    type: componenteTypeDataSchema,
    label: 'Flag that checks if the SentinelComponent button is shown'
  },
  SharedComponent: {
    type: componenteTypeDataSchema,
    label: 'Flag that checks if the SharedComponent button is shown'
  },
  ShowMapDeckLidarComponent: {
    type: componenteTypeDataSchema,
    label: 'Flag that checks if the ShowMapDeckLidarComponent button is shown'
  },
  ViewLinksComponent: {
    type: componenteTypeDataSchema,
    label: 'Flag that checks if the ViewLinksComponent button is shown'
  }
});

const intervalNotesDataSchema = new SimpleSchema({
  show: {
    type: Boolean,
    label: 'The name to show of the note interval',
    optional: true
  },
  value: {
    type: Number,
    label: 'The value of the note interval',
    optional: true
  }
});

export const notesDataSchema = new SimpleSchema({
  centerNotes: {
    type: Boolean,
    label: 'centerNotes',
    optional: true
  },
  centerNotesDefault: {
    type: Boolean,
    label: 'centerNotes',
    optional: true
  },
  entWine: {
    type: Array,
    label: 'entWine',
    optional: true
  },
  'entWine.$': {
    type: entWineDataSchema,
    label: 'entWine',
    optional: true
  },
  interval: {
    type: intervalNotesDataSchema,
    label: 'interval',
    optional: true
  },
  showGeomotion: {
    type: Boolean,
    label: 'showGeomotion',
    optional: true
  },
  showGeomotionPublic: {
    type: Boolean,
    label: 'showGeomotionPublic',
    optional: true
  },
  showStreetView: {
    type: Boolean,
    label: 'showStreetView',
    optional: true
  },
  showVideo: {
    type: Boolean,
    label: 'showVideo',
    optional: true
  },
  videos: {
    type: Array,
    label: 'videos',
    optional: true
  },
  'videos.$': {
    type: String,
    label: 'videos'
  }
});

export const settingsAdvancedMapDataSchema = new SimpleSchema({
  buttonSettings: {
    type: Object,
    label: 'The settings of the buttons.',
    optional: true
  },
  'buttonSettings.showDownloadLayer': {
    type: Boolean,
    label: 'Show de button of download layer.'
  },
  components: {
    type: selectedComponentsDataSchema,
    label: 'The components of the advanced map.',
    optional: true
  },
  connections: {
    type: settingsConnectionSchema,
    label: 'The connetion settings of the download component.',
    optional: true
  },
  defaultComponent: {
    type: String,
    label: 'The first component to show when open advancedMap',
    optional: true
  },
  directButtons: {
    type: directButtonsDataSchema,
    label: 'The first component to show when open advancedMap',
    optional: true
  },
  fileTools: {
    type: Object,
    label: 'The first component to show when open advancedMap',
    optional: true,
    blackbox: true
  },
  ipsOfIsl: {
    type: Array,
    label: 'The list of ips.',
    optional: true
  },
  'ipsOfIsl.$': {
    type: Object,
    label: 'The ips url to use in the ISl component.',
    blackbox: true // teide crear esquema
  },
  mapTypes: {
    type: Array,
    label: 'The list of map types.',
    optional: true
  },
  'mapTypes.$': {
    type: mapTypeDataSchema,
    label: 'An object containing the list of map types.'
  },
  notes: {
    type: notesDataSchema,
    label: 'Array with the settings of Notes Component',
    optional: true
  },
  sizeDrawer: {
    type: sizeDrawerDataSchema,
    label: 'The size of the drawer.',
    optional: true
  },
  showTooltip: {
    type: Boolean,
    label: 'Show tooltip.',
    optional: true
  },
  textSettings: {
    type: Object,
    label: 'The settings of the text.',
    optional: true
  },
  'textSettings.title': {
    type: Number,
    label: 'The size of title.'
  },
  'textSettings.description': {
    type: Number,
    label: 'The size of the description.',
    optional: true
  },
  'textSettings.descriptionHeight': {
    type: Number,
    label: 'The size of the description.',
    optional: true
  },
  'textSettings.font': {
    type: String,
    label: 'Font text in IntroductionComponent'
  },
  'textSettings.logo': {
    type: String,
    label: 'The url of the logo in IntroductionComponent',
    optional: true
  },
  viewLinks: {
    type: Array,
    label: 'Array with the values of ViewLinksComponent',
    optional: true
  },
  'viewLinks.$': {
    type: Object,
    label: 'Objet of the link list.'
  },
  'viewLinks.$.type': {
    type: String,
    label: 'Type value of ViewLinksComponent'
  },
  'viewLinks.$.value': {
    type: String,
    label: 'Url value of ViewLinksComponent'
  },
  'viewLinks.$.name': {
    type: String,
    label: 'Url value of ViewLinksComponent'
  }
});
